// App and analytics frames can arrive after the initial page has loaded.
function initializeFrameTitles() {
  const nameFrame = frame => {
    if (frame.title.trim()) return;
    const existingLabel = frame.getAttribute('aria-label')?.trim();
    if (existingLabel) { frame.title = existingLabel; return; }
    let source;
    try { source = new URL(frame.getAttribute('src') || '', location.href); }
    catch { return; }
    if (source.origin === location.origin && source.pathname.startsWith('/web-pixels@')) {
      frame.title = 'Shopify analytics sandbox';
    } else if (['www.youtube.com', 'youtube.com', 'www.youtube-nocookie.com', 'youtube-nocookie.com'].includes(source.hostname) && source.pathname.startsWith('/embed/')) {
      const heading = frame.closest('article')?.querySelector('h1,h2') || document.querySelector('main h1');
      frame.title = heading ? `YouTube video: ${heading.textContent.trim()}` : 'YouTube video player';
    }
  };
  const inspect = node => {
    if (node.nodeType !== Node.ELEMENT_NODE && node.nodeType !== Node.DOCUMENT_NODE) return;
    if (node.matches?.('iframe')) nameFrame(node);
    node.querySelectorAll('iframe').forEach(nameFrame);
  };
  inspect(document);
  new MutationObserver(records => {
    records.forEach(record => {
      if (record.type === 'attributes') nameFrame(record.target);
      else record.addedNodes.forEach(inspect);
    });
  }).observe(document.documentElement, {childList: true, subtree: true, attributes: true, attributeFilter: ['src']});
}
initializeFrameTitles();

// Shopify Forms renders its controls inside an open shadow root.
function initializeFormAccessibility() {
  const observed = new WeakSet();
  const enhanceControls = root => {
    const purposes = {first_name: 'given-name', last_name: 'family-name', email: 'email'};
    root.querySelectorAll('input').forEach(input => {
      const purpose = purposes[input.id];
      if (purpose && input.autocomplete !== purpose) input.autocomplete = purpose;
    });
    root.querySelectorAll('button[role="combobox"][aria-label] svg, button[role="combobox"][aria-labelledby] svg').forEach(icon => {
      if (icon.hasAttribute('aria-label') || icon.hasAttribute('aria-labelledby') || icon.querySelector('title')) return;
      icon.setAttribute('aria-hidden', 'true');
      icon.setAttribute('focusable', 'false');
    });
  };
  const connect = () => {
    document.querySelectorAll('shopify-forms-embed').forEach(host => {
      const root = host.shadowRoot;
      if (!root || observed.has(root)) return;
      observed.add(root);
      enhanceControls(root);
      new MutationObserver(() => enhanceControls(root)).observe(root, {childList: true, subtree: true, attributes: true, attributeFilter: ['autocomplete']});
    });
  };
  customElements.whenDefined('shopify-forms-embed').then(() => {
    requestAnimationFrame(connect);
    new MutationObserver(connect).observe(document.documentElement, {childList: true, subtree: true});
  });
}
initializeFormAccessibility();

function initializeProductGalleries(scope = document) {
  scope.querySelectorAll('[data-product-gallery]').forEach(gallery => {
    if (gallery.dataset.initialized) return;
    const image = gallery.querySelector('.product-gallery-main');
    const thumbnails = [...gallery.querySelectorAll('[data-photo-url]')];
    if (!image || thumbnails.length < 2) return;
    gallery.dataset.initialized = 'true';
    let index = 0;
    const show = nextIndex => {
      index = (nextIndex + thumbnails.length) % thumbnails.length;
      image.removeAttribute('srcset');
      image.removeAttribute('sizes');
      image.src = thumbnails[index].dataset.photoUrl;
      image.alt = thumbnails[index].dataset.photoAlt;
      thumbnails.forEach((thumbnail, i) => thumbnail.setAttribute('aria-current', String(i === index)));
      gallery.querySelector('.product-gallery-count').textContent = `${index + 1} / ${thumbnails.length}`;
      thumbnails[index].scrollIntoView({behavior: 'smooth', block: 'nearest', inline: 'nearest'});
    };
    thumbnails.forEach((thumbnail, i) => thumbnail.addEventListener('click', event => {
      event.preventDefault();
      show(i);
    }));
    gallery.querySelector('.product-gallery-prev').addEventListener('click', () => show(index - 1));
    gallery.querySelector('.product-gallery-next').addEventListener('click', () => show(index + 1));
    gallery.querySelectorAll('.product-gallery-arrow').forEach(button => { button.hidden = false; });
    gallery.addEventListener('keydown', event => {
      if (!['ArrowLeft', 'ArrowRight'].includes(event.key)) return;
      event.preventDefault();
      show(index + (event.key === 'ArrowLeft' ? -1 : 1));
    });
  });
}

function initializeWatchGalleries(scope = document) {
  scope.querySelectorAll('[data-watch-gallery]').forEach(gallery => {
    if (gallery.dataset.initialized) return;
    const data = gallery.querySelector('[data-gallery-images]');
    const image = gallery.querySelector('img');
    const controls = gallery.querySelector('.watch-photo-controls');
    if (!data || !image || !controls) return;
    const photos = JSON.parse(data.textContent);
    if (photos.length < 2) return;
    gallery.dataset.initialized = 'true';
    let index = 0;
    const show = direction => {
      index = (index + direction + photos.length) % photos.length;
      image.removeAttribute('srcset');
      image.removeAttribute('sizes');
      image.src = photos[index];
      controls.querySelector('.watch-photo-count').textContent = `${index + 1} / ${photos.length}`;
    };
    controls.querySelector('.watch-photo-previous').addEventListener('click', () => show(-1));
    controls.querySelector('.watch-photo-next').addEventListener('click', () => show(1));
    controls.hidden = false;
  });
}
function initializeFeaturedQuickView(scope = document) {
  scope.querySelectorAll('.product-carousel').forEach(carousel => {
    if (carousel.dataset.quickViewInitialized) return;
    carousel.dataset.quickViewInitialized = 'true';
    carousel.addEventListener('click', async event => {
      const link = event.target.closest('[data-quick-view-url]');
      if (!link || event.ctrlKey || event.metaKey || event.shiftKey || event.altKey || event.button !== 0) return;
      event.preventDefault();
      const productUrl = link.dataset.quickViewUrl;
      let dialog = document.querySelector('[data-featured-quick-view]');
      if (!dialog) {
        dialog = document.createElement('dialog');
        dialog.className = 'featured-quick-view';
        dialog.dataset.featuredQuickView = '';
        dialog.setAttribute('aria-label', 'Watch details');
        dialog.innerHTML = '<button class="quick-view-close" type="button" aria-label="Close watch details">×</button><div class="quick-view-content" aria-live="polite"></div>';
        document.body.append(dialog);
        dialog.querySelector('button').addEventListener('click', () => dialog.close());
        dialog.addEventListener('click', click => { if (click.target === dialog) { const bounds = dialog.getBoundingClientRect(); if (click.clientX < bounds.left || click.clientX > bounds.right || click.clientY < bounds.top || click.clientY > bounds.bottom) dialog.close(); } });
        dialog.addEventListener('close', () => { document.documentElement.classList.remove('quick-view-open'); dialog.requestController?.abort(); });
      }
      dialog.requestController?.abort();
      const controller = new AbortController();
      dialog.requestController = controller;
      const content = dialog.querySelector('.quick-view-content');
      content.textContent = 'Loading watch details…';
      if (!dialog.open) dialog.showModal();
      document.documentElement.classList.add('quick-view-open');
      try {
        const response = await fetch(productUrl, {signal: controller.signal});
        if (!response.ok) throw new Error('Unable to load watch');
        const page = new DOMParser().parseFromString(await response.text(), 'text/html');
        const details = page.querySelector('.watch-detail');
        if (!details) throw new Error('Watch details missing');
        if (controller.signal.aborted) return;
        details.querySelectorAll('script').forEach(script => script.remove());
        details.querySelectorAll('[data-initialized]').forEach(element => {
          delete element.dataset.initialized;
        });
        details.querySelectorAll('a[href="#inquiry"]').forEach(action => { action.href = productUrl.split('#')[0] + '#inquiry'; });
        content.replaceChildren(details);
        initializeProductGalleries(content);
      } catch (error) {
        if (controller.signal.aborted) return;
        content.textContent = 'Watch details could not be loaded. ';
        const fallback = document.createElement('a');
        fallback.href = productUrl;
        fallback.textContent = 'View this watch';
        content.append(fallback);
      }
    });
  });
}

// Keep Shopify preview parameters, but start changed filters on page one.
function collectionFilterUrl(currentUrl, entries) {
  const url = new URL(currentUrl);
  for (const key of [...url.searchParams.keys()]) {
    if (key.startsWith('filter.') || key === 'sort_by' || key === 'page') url.searchParams.delete(key);
  }
  for (const [name, value] of entries) {
    if ((name.startsWith('filter.') || name === 'sort_by') && String(value).trim() !== '') url.searchParams.append(name, value);
  }
  url.hash = '';
  return url.toString();
}

function initializeCollectionFilters(scope = document) {
  scope.querySelectorAll('[data-watch-collection]').forEach(section => {
    if (section.dataset.filtersInitialized) return;
    section.dataset.filtersInitialized = 'true';
    const form = section.querySelector('.collection-filters');
    const dialog = section.querySelector('.collection-filter-dialog');
    const opener = section.querySelector('[data-filter-open]');
    opener.hidden = false;
    let closeTimer;
    const finishClose = () => {
      clearTimeout(closeTimer);
      dialog.close();
    };
    const closeDrawer = () => {
      if (!dialog.open || dialog.classList.contains('is-closing')) return;
      if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
        finishClose();
        return;
      }
      dialog.classList.add('is-closing');
      closeTimer = setTimeout(finishClose, 240);
    };
    opener.addEventListener('click', () => {
      dialog.classList.remove('is-closing');
      dialog.showModal();
      opener.setAttribute('aria-expanded', 'true');
      document.documentElement.classList.add('collection-filters-open');
    });
    dialog.querySelectorAll('[data-filter-close]').forEach(button => button.addEventListener('click', closeDrawer));
    dialog.addEventListener('cancel', event => { event.preventDefault(); closeDrawer(); });
    dialog.addEventListener('animationend', event => {
      if (event.target === dialog && event.animationName === 'filter-drawer-out') finishClose();
    });
    dialog.addEventListener('close', () => {
      clearTimeout(closeTimer);
      dialog.classList.remove('is-closing');
      document.documentElement.classList.remove('collection-filters-open');
      opener.setAttribute('aria-expanded', 'false');
    });
    dialog.addEventListener('click', event => {
      if (event.target !== dialog) return;
      const bounds = dialog.getBoundingClientRect();
      if (event.clientX < bounds.left || event.clientX > bounds.right || event.clientY < bounds.top || event.clientY > bounds.bottom) closeDrawer();
    });
    form.addEventListener('submit', event => {
      event.preventDefault();
      window.location.assign(collectionFilterUrl(window.location.href, new FormData(form)));
    });
    form.hidden = false;
  });
}

document.addEventListener('shopify:section:load', event => {
  initializeCarousels(event.target);
  initializeCollectionFilters(event.target);
  initializeFeaturedQuickView(event.target);
  initializeWatchGalleries(event.target);
  initializeProductGalleries(event.target);
});
document.addEventListener('DOMContentLoaded', () => {
  const menu = document.querySelector('#mobile-menu');
  const menuButton = document.querySelector('.mobile-nav');
  if (menu && menuButton) {
    menuButton.addEventListener('click', () => {
      menu.showModal();
      menuButton.setAttribute('aria-expanded', 'true');
      document.documentElement.classList.add('mobile-menu-open');
    });
    menu.querySelector('[data-menu-close]').addEventListener('click', () => menu.close());
    menu.querySelectorAll('a').forEach(link => link.addEventListener('click', () => menu.close()));
    menu.addEventListener('click', event => {
      if (event.target !== menu) return;
      const bounds = menu.getBoundingClientRect();
      if (event.clientX < bounds.left || event.clientX > bounds.right || event.clientY < bounds.top || event.clientY > bounds.bottom) menu.close();
    });
    menu.addEventListener('close', () => {
      menuButton.setAttribute('aria-expanded', 'false');
      document.documentElement.classList.remove('mobile-menu-open');
    });
    window.addEventListener('resize', () => { if (window.innerWidth > 850 && menu.open) menu.close(); });
  }
  initializeCollectionFilters();
  initializeFeaturedQuickView();
  initializeWatchGalleries();
  initializeProductGalleries();
  initializeCarousels();
});

const carouselCleanup = new Map();
function initializeCarousels(scope = document) {
  scope.querySelectorAll('.product-carousel,.journal-carousel').forEach(wrapper => {
    if (wrapper.dataset.carouselInitialized) return;
    const track = wrapper.querySelector('.products,.journal');
    const previous = wrapper.querySelector('.previous');
    const next = wrapper.querySelector('.next');
    if (!track || !previous || !next) return;
    wrapper.dataset.carouselInitialized = 'true';
    const update = () => {previous.disabled = track.scrollLeft <= 2; next.disabled = track.scrollLeft >= track.scrollWidth-track.clientWidth-2;};
    const move = direction => {const card=track.querySelector('article'); if(card) track.scrollBy({left:direction*(card.offsetWidth+10),behavior:'smooth'});};
    previous.addEventListener('click',()=>move(-1)); next.addEventListener('click',()=>move(1));
    track.addEventListener('scroll',update,{passive:true});window.addEventListener('resize',update);
    carouselCleanup.set(wrapper, () => window.removeEventListener('resize', update));
    requestAnimationFrame(update);
  });
}
document.addEventListener('shopify:section:unload', event => {
  for (const [wrapper, cleanup] of carouselCleanup) {
    if (event.target.contains(wrapper)) { cleanup(); carouselCleanup.delete(wrapper); }
  }
});
